// src/pages/Contact.jsx
import React, { useState } from 'react';
import ODFLogo from '../components/ODFLogo';
import { Instagram, Mail, CheckCircle2 } from 'lucide-react';

// Inline X / Twitter icon (lucide doesn't ship one)
const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);

export default function Contact({ onBack, onSignIn }) {
  const [form,    setForm]    = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true); setError('');
    try {
      const res = await fetch('https://formspree.io/f/mvzlawvw', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name:    form.name,
          email:   form.email,
          subject: form.subject,
          message: form.message,
        }),
      });
      if (res.ok) {
        setSent(true);
      } else {
        const data = await res.json();
        setError(data?.errors?.[0]?.message || 'Something went wrong. Please reach out on Instagram @odftrades.');
      }
    } catch (err) {
      setError('Could not send message. Please reach out on Instagram @odftrades.');
    }
    setSending(false);
  };

  const inp = {
    width: '100%', background: '#0a0a0f',
    border: '1px solid #1e1e2e', borderRadius: 8,
    color: '#f0f0f5', padding: '12px 14px',
    fontSize: 14, fontFamily: "'IBM Plex Sans', sans-serif",
    outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  const SOCIALS = [
    { label: 'Instagram',  handle: '@odftrades',          url: 'https://instagram.com/odftrades', Icon: Instagram },
    { label: 'X / Twitter', handle: '@odftrades',         url: 'https://x.com/odftrades',         Icon: XIcon },
    { label: 'Email',      handle: 'info@odftrades.com', url: 'mailto:info@odftrades.com',       Icon: Mail },
  ];

  return (
    <div style={{ background: '#0a0a0f', minHeight: '100vh', color: '#f0f0f5', fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #1e1e2e', padding: '0 40px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ cursor: 'pointer' }} onClick={onBack}><ODFLogo size="compact" /></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8888a8', fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif" }}>← Back</button>
          <button onClick={onSignIn} style={{ background: '#00e5a0', color: '#0a0a0f', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'IBM Plex Sans', sans-serif" }}>Sign In →</button>
        </div>
      </nav>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '60px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>Get in touch</div>
          <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 16px' }}>Contact ODFTrades</h1>
          <p style={{ fontSize: 15, color: '#8888a8', maxWidth: 480, margin: '0 auto', lineHeight: 1.7 }}>
            Have a question, suggestion, or want to learn more? Reach out through any channel below.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, alignItems: 'start' }}>
          {/* Form */}
          <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 16, padding: '32px 28px' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#f0f0f5', marginBottom: 24 }}>Send a message</div>

            {sent ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00e5a0' }}>
                    <CheckCircle2 size={28} />
                  </div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#00e5a0', marginBottom: 8 }}>Message sent!</div>
                <div style={{ fontSize: 13, color: '#8888a8' }}>We'll get back to you as soon as possible.</div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { key: 'name', label: 'Name', type: 'text', placeholder: 'Your name' },
                  { key: 'email', label: 'Email', type: 'email', placeholder: 'your@email.com' },
                  { key: 'subject', label: 'Subject', type: 'text', placeholder: 'e.g. Question about the platform' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: 11, color: '#8888a8', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: 6 }}>{f.label}</label>
                    <input type={f.type} placeholder={f.placeholder} required value={form[f.key]}
                      onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={inp}
                      onFocus={e => e.target.style.borderColor = '#00e5a0'}
                      onBlur={e => e.target.style.borderColor = '#1e1e2e'} />
                  </div>
                ))}
                <div>
                  <label style={{ fontSize: 11, color: '#8888a8', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: 6 }}>Message</label>
                  <textarea placeholder="Your message, question or suggestion..." required value={form.message}
                    onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                    rows={5} style={{ ...inp, resize: 'vertical', minHeight: 120 }}
                    onFocus={e => e.target.style.borderColor = '#00e5a0'}
                    onBlur={e => e.target.style.borderColor = '#1e1e2e'} />
                </div>
                {error && <div style={{ fontSize: 12, color: '#ff4d6d', background: 'rgba(255,77,109,0.08)', border: '1px solid rgba(255,77,109,0.2)', borderRadius: 7, padding: '10px 12px' }}>{error}</div>}
                <button type="submit" disabled={sending} style={{
                  background: '#00e5a0', color: '#0a0a0f', border: 'none', borderRadius: 8,
                  padding: '13px', fontSize: 14, fontWeight: 700,
                  cursor: sending ? 'not-allowed' : 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif", opacity: sending ? 0.7 : 1, transition: 'all 0.2s',
                }}>
                  {sending ? 'Sending...' : 'Send Message →'}
                </button>
              </form>
            )}
          </div>

          {/* Social + info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 16, padding: '28px 24px' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#f0f0f5', marginBottom: 20 }}>Connect with us</div>
              {SOCIALS.map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noreferrer" style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '12px 0', borderBottom: i < SOCIALS.length - 1 ? '1px solid #1e1e2e' : 'none',
                  textDecoration: 'none', transition: 'opacity 0.2s',
                }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                  <div style={{ width: 36, height: 36, background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.15)', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00e5a0' }}>
                    <s.Icon size={16} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#f0f0f5' }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: '#00e5a0', fontFamily: "'DM Mono', monospace" }}>{s.handle}</div>
                  </div>
                  <span style={{ marginLeft: 'auto', color: '#2a2a3e', fontSize: 16 }}>→</span>
                </a>
              ))}
            </div>

            <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 16, padding: '24px' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#f0f0f5', marginBottom: 12 }}>Response time</div>
              <div style={{ fontSize: 13, color: '#8888a8', lineHeight: 1.7 }}>
                We aim to respond to all inquiries within 24-48 hours. For faster responses, reach out on Instagram.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
